import { Injectable } from "@angular/core";
import { BaseApiService } from "../base-api.service";
import { HttpClient } from "@angular/common/http";
import { delay, firstValueFrom, map, Observable } from "rxjs";
import { environment } from "@beyou/environments/environment";
import { SessionCategory, SessionCategoryBase, SessionCategoryTranslation } from "../../models/sessions/session-category.model";
import { SessionCategoryMapper } from "../../mappers/sessions/session-category.mapper";
import { SessionCategoryDto } from "../../dto/sessions/session-category.dto";
import { SessionCategoryTranslationsDto } from "../../dto/sessions/session-category-translations.dto";
import { SessionCategoryTranslationMapper } from "../../mappers/sessions/session-category-translation.mapper";
import { ExportResultDto } from "../../dto/export-result.dto";

@Injectable({
  providedIn: 'root'
})
export class SessionCategoryApiService extends BaseApiService {

  constructor(http: HttpClient) {
    super(http);
  }

  public getAll(): Observable<SessionCategory[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories`;
    return this.getAllPagedData<SessionCategoryDto>(apiUrl).pipe(
      map((response) => {
        return response.map(item => {
          return SessionCategoryMapper.toModel(item);
        })
      })
    );
  }

  public async add(sessionCategory: SessionCategoryBase): Promise<SessionCategory> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories`;
    const dto = SessionCategoryMapper.toCreateDto(sessionCategory);
    console.debug('new session category dto', dto);
    return await firstValueFrom(this.http.post<SessionCategoryDto>(apiUrl, dto).pipe(
      delay(3000), // Allow time for the cover image to be resized on S3
      map((response) => {
        console.debug('session category added: ', response.id);
        return SessionCategoryMapper.toModel(response);
      })
    ));
  }

  public async update(sessionCategory: SessionCategory): Promise<SessionCategory> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories/${sessionCategory.id}`;
    const dto = SessionCategoryMapper.toUpdateDto(sessionCategory);
    return await firstValueFrom(this.http.put<SessionCategoryDto>(apiUrl, dto).pipe(
      delay(3000), // Allow time for the cover image to be resized on S3
      map((response) => {
        console.debug('session category updated: ', response.id);
        return SessionCategoryMapper.toModel(response);
      })
    ));
  }

  public async delete(id: number): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories/${id}`;
    try {
      await firstValueFrom(this.http.delete(apiUrl));
      console.log('Session category deleted: ', id);
    } catch (error) {
      console.error('Error deleting session category with id: ', id, error);
      throw error; // Re-throw the error to handle it in the calling code
    }
  }

  public async getTranslations(id: number): Promise<SessionCategoryTranslation[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories/${id}/translations`;
    return await firstValueFrom(this.http.get<SessionCategoryTranslationsDto>(apiUrl).pipe(
      map((response) => {
        console.debug('session category translations retrieved: ', id);
        return response.translations.map(item => SessionCategoryTranslationMapper.toModel(item));
      })
    ));
  }

  public async updateTranslations(id: number, translations: SessionCategoryTranslation[]): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories/${id}/translations`;
    const dto = {
      translations: translations.map(item => SessionCategoryTranslationMapper.toDto(item))
    };
    await firstValueFrom(this.http.post(apiUrl, dto));
    console.debug('session category translations updated: ', id);
  }

  public async export(languageId: number): Promise<ExportResultDto> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories/export?language_id=${languageId}`;
    return await firstValueFrom(this.http.get<ExportResultDto>(apiUrl));
  }

  public async import(file: File): Promise<void> {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const apiUrl = `${environment.adminApiBaseUrl}/session-categories/import`;
    await firstValueFrom(this.http.post(apiUrl, uploadData));
  }
}
